/**
 * Detecção de capacidades de screenshot por plataforma.
 *
 * <p>Objetivo: mesma build Efflux se adapta a Consumer, SSSP (signage) e
 * browser — escolhendo o método mais seguro/capaz em runtime.</p>
 *
 * <p>Consumer Tizen: vídeo em HW overlay → NÃO fazer readback por
 * {@code drawImage} (crash). Após esgotar readback, usa thumbnail estático
 * do backend; se indisponível, placeholder informativo.</p>
 *
 * <p>SSSP (signage comercial): mesma limitação de overlay no {@code <video>},
 * mas a firmware pode expor {@code getDisplayMedia} (captura da tela
 * composta, incluindo vídeo + overlays). Probe em runtime; se disponível,
 * usa antes do fallback DOM.</p>
 */

export type TizenDeviceClass = 'consumer' | 'signage' | 'unknown';

/**
 * Estratégia de captura escolhida em runtime.
 *
 * <p>{@code android-native} (PixelCopy via plugin Capacitor) permanece reservado
 * para fallback futuro se telemetria de {@code blackRatio} apontar falha em
 * algum modelo da frota. Hoje Android usa {@code dom-plus-video} — validado
 * em Tab A7 SM-T505 (Android 12).</p>
 */
export type CapturePreferredMethod =
  | 'display-media'
  | 'dom-plus-video'
  | 'dom-plus-placeholder';

export interface CapturePlatformCapabilities {
  platform: 'browser' | 'android' | 'tizen' | 'webos' | 'unknown';
  tizenClass: TizenDeviceClass | null;
  model: string | null;
  hasB2bApis: boolean;
  hasDisplayMedia: boolean;
  /** Readback por vídeo individual no Tizen — desligado (crash em consumer). */
  skipTizenVideoReadback: boolean;
  preferredMethod: CapturePreferredMethod;
}

/** Prefixos de modelo Samsung Smart Signage Platform (SSSP). */
const SIGNAGE_MODEL_PREFIXES = [
  'QB',
  'QM',
  'QH',
  'OH',
  'OL',
  'OM',
  'PH',
  'PM',
  'WA',
  'DB',
] as const;

let cachedCaps: CapturePlatformCapabilities | null = null;

function envPlatform(): string | undefined {
  try {
    return import.meta.env?.VITE_PLATFORM;
  } catch {
    return undefined;
  }
}

export function isTizenRuntime(): boolean {
  if (envPlatform() === 'tizen') return true;
  return (
    typeof window !== 'undefined' &&
    typeof (window as { tizen?: unknown }).tizen !== 'undefined'
  );
}

export function getTizenModel(): string | null {
  try {
    const pi = (
      window as {
        webapis?: {
          productinfo?: {
            getRealModel?: () => string;
            getModel?: () => string;
            getModelCode?: () => string;
          };
        };
      }
    ).webapis?.productinfo;
    return (
      pi?.getRealModel?.() || pi?.getModel?.() || pi?.getModelCode?.() || null
    );
  } catch {
    return null;
  }
}

function hasB2bApisLoaded(): boolean {
  try {
    const w = window as {
      b2bapis?: Record<string, unknown>;
    };
    if (!w.b2bapis) return false;
    return (
      typeof w.b2bapis.brightness === 'object' ||
      typeof w.b2bapis.b2bcontrol === 'object'
    );
  } catch {
    return false;
  }
}

/** Classifica TV Samsung: consumer residencial vs linha comercial SSSP. */
export function detectTizenDeviceClass(
  model?: string | null,
): TizenDeviceClass {
  if (hasB2bApisLoaded()) return 'signage';

  const m = (model ?? getTizenModel() ?? '').toUpperCase().replace(/\s/g, '');
  if (m) {
    for (const prefix of SIGNAGE_MODEL_PREFIXES) {
      if (m.startsWith(prefix)) return 'signage';
    }
    // Consumer típico: UN*, QN*, S*, AU*, etc.
    if (/^(UN|QN|AU|S[0-9]|TU|CU|RU|NU)/.test(m)) return 'consumer';
  }

  if (isTizenRuntime()) return 'consumer';
  return 'unknown';
}

export function hasDisplayMediaApi(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getDisplayMedia === 'function'
  );
}

/**
 * Probe único (cacheado) — chamado no boot ou antes da 1ª captura.
 * Logar o resultado ajuda a validar SSSP vs consumer em campo.
 */
export function probeCapturePlatformCapabilities(): CapturePlatformCapabilities {
  if (cachedCaps) return cachedCaps;

  const vite = envPlatform();
  let platform: CapturePlatformCapabilities['platform'] = 'unknown';
  if (vite === 'android' || vite === 'ios') platform = 'android';
  else if (vite === 'tizen' || isTizenRuntime()) platform = 'tizen';
  else if (vite === 'webos') platform = 'webos';
  else if (vite === 'browser' || vite === 'web') platform = 'browser';
  else if (typeof window !== 'undefined') platform = 'browser';

  const model = platform === 'tizen' ? getTizenModel() : null;
  const tizenClass =
    platform === 'tizen' ? detectTizenDeviceClass(model) : null;
  const hasB2bApis = platform === 'tizen' && hasB2bApisLoaded();
  const hasDisplayMedia = hasDisplayMediaApi();

  // Android/Capacitor: html2canvas + drawImage(video) no WebView Chromium
  // (mesmo caminho do browser). Não há plugin nativo — PixelCopy só se
  // a frota reportar frames pretos em modelos específicos.
  let preferredMethod: CapturePreferredMethod = 'dom-plus-video';
  if (platform === 'tizen') {
    if (tizenClass === 'signage' && hasDisplayMedia) {
      preferredMethod = 'display-media';
    } else {
      preferredMethod = 'dom-plus-placeholder';
    }
  } else if (platform === 'browser' && hasDisplayMedia) {
    preferredMethod = 'dom-plus-video';
  }

  cachedCaps = {
    platform,
    tizenClass,
    model,
    hasB2bApis,
    hasDisplayMedia,
    skipTizenVideoReadback: platform === 'tizen',
    preferredMethod,
  };

  console.info('[capturePlatform] capabilities:', JSON.stringify(cachedCaps));
  return cachedCaps;
}

/** Limpa cache (só testes). */
export function resetCapturePlatformCacheForTest(): void {
  cachedCaps = null;
}

/**
 * Tizen consumer: NUNCA readback por vídeo (crash comprovado 2026-05-29).
 * SSSP: mesmo skip no loop por vídeo; tentar {@code getDisplayMedia} antes.
 */
export function shouldSkipTizenVideoReadback(): boolean {
  return probeCapturePlatformCapabilities().skipTizenVideoReadback;
}

export function shouldTryDisplayMediaCapture(): boolean {
  const caps = probeCapturePlatformCapabilities();
  // Apenas SSSP: em browser getDisplayMedia abre picker de permissão (UX ruim).
  return caps.hasDisplayMedia && caps.tizenClass === 'signage';
}

/**
 * Captura a tela composta via {@code getDisplayMedia} (inclui HW overlay de
 * vídeo quando a plataforma permite — candidato principal em SSSP).
 *
 * @returns canvas escalado ou null se indisponível / negado / erro
 */
export async function tryCaptureViaDisplayMedia(
  maxWidth = 1280,
  maxHeight = 720,
): Promise<HTMLCanvasElement | null> {
  if (!hasDisplayMediaApi()) return null;

  let stream: MediaStream | null = null;
  try {
    stream = await navigator.mediaDevices.getDisplayMedia({
      video: { frameRate: 1 },
      audio: false,
    });

    const track = stream.getVideoTracks()[0];
    if (!track) return null;

    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.srcObject = stream;

    await new Promise<void>((resolve, reject) => {
      video.onloadedmetadata = () => resolve();
      video.onerror = () => reject(new Error('video metadata error'));
      void video.play().catch(reject);
    });

    // Um frame de estabilização
    await new Promise((r) =>
      requestAnimationFrame(() => requestAnimationFrame(r)),
    );

    let w = video.videoWidth;
    let h = video.videoHeight;
    if (w <= 0 || h <= 0) return null;

    const scale = Math.min(1, maxWidth / w, maxHeight / h);
    w = Math.round(w * scale);
    h = Math.round(h * scale);

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, w, h);
    return canvas;
  } catch (err) {
    console.warn('[capturePlatform] getDisplayMedia falhou:', err);
    return null;
  } finally {
    stream?.getTracks().forEach((t) => t.stop());
  }
}
